import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ChevronRight, UserX, VolumeX, Eye, Download, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";

/** Safety & privacy hub — Yubo-style. */
export default function SettingsPrivacy() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [blockedCount, setBlockedCount] = useState(null);
  const [safeBlur, setSafeBlur] = useState(user?.safe_blur !== false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/blocked")
      .then(({ data }) => setBlockedCount(Array.isArray(data) ? data.length : 0))
      .catch(() => setBlockedCount(0));
  }, []);

  const toggleSafeBlur = async (v) => {
    setSafeBlur(v);
    try {
      await api.patch("/users/me/privacy", { safe_blur: v });
      await refresh();
    } catch (e) {
      setSafeBlur(!v);
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    }
  };

  const requestData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/users/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tanta-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("settings.dataDownloaded"));
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally { setExporting(false); }
  };

  const isGoogle = user?.auth_provider === "google";

  return (
    <div className="min-h-screen bg-white text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="settings-privacy-page">
      <div className="sticky top-0 z-30 bg-white border-b border-[#E2DFD4]">
        <div className="flex items-center gap-2 px-5 py-5">
          <button onClick={() => nav(-1)} className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-[#F4F1E8]" data-testid="privacy-back" aria-label="Back">
            <ArrowLeft className="w-7 h-7" strokeWidth={3} />
          </button>
          <h1 className="font-display font-black text-[28px] tracking-tight">{t("settings.privacy")}</h1>
        </div>
      </div>

      <ul className="divide-y divide-[#F0EDE3]">
        {/* Blocked users */}
        <li>
          <button onClick={() => nav("/settings/privacy/blocked")} className="w-full flex items-center gap-4 px-5 py-5 text-left hover:bg-[#FAF8F2] transition-colors" data-testid="privacy-blocked">
            <UserX className="w-6 h-6 text-[#3D424B]" strokeWidth={2} />
            <span className="font-display font-semibold text-lg flex-1">{t("settings.blockedUsers")}</span>
            {blockedCount === null
              ? <Loader2 className="w-4 h-4 animate-spin text-[#B9BDC7]" />
              : <span className="text-[#0F9D8F] font-black">{blockedCount}</span>}
            <ChevronRight className="w-5 h-5 text-[#B9BDC7]" strokeWidth={2.5} />
          </button>
        </li>

        {/* Muted words */}
        <li>
          <button onClick={() => nav("/settings/privacy/muted")} className="w-full flex items-start gap-4 px-5 py-5 text-left hover:bg-[#FAF8F2] transition-colors" data-testid="privacy-muted">
            <VolumeX className="w-6 h-6 text-[#3D424B] mt-0.5" strokeWidth={2} />
            <span className="flex-1">
              <span className="font-display font-semibold text-lg block">{t("settings.mutedWords")}</span>
              <span className="text-sm text-[#7C828D]">{t("settings.mutedWordsDesc")}</span>
            </span>
            <ChevronRight className="w-5 h-5 text-[#B9BDC7] mt-1.5" strokeWidth={2.5} />
          </button>
        </li>

        {/* SafeBlur toggle */}
        <li className="flex items-start gap-4 px-5 py-5" data-testid="privacy-safeblur">
          <Eye className="w-6 h-6 text-[#3D424B] mt-0.5" strokeWidth={2} />
          <div className="flex-1">
            <span className="font-display font-semibold text-lg block">{t("settings.safeBlur")}</span>
            <span className="text-sm text-[#7C828D]">{t("settings.safeBlurDesc")}</span>
          </div>
          <Switch checked={safeBlur} onCheckedChange={toggleSafeBlur} data-testid="safeblur-switch" />
        </li>

        {/* Privacy preferences (hide profile, discovery prefs) */}
        <li>
          <button onClick={() => nav("/settings/people-preferences")} className="w-full flex items-center gap-4 px-5 py-5 text-left hover:bg-[#FAF8F2] transition-colors" data-testid="privacy-prefs">
            <Eye className="w-6 h-6 text-[#3D424B]" strokeWidth={2} />
            <span className="font-display font-semibold text-lg flex-1">{t("settings.privacyPrefs")}</span>
            <ChevronRight className="w-5 h-5 text-[#B9BDC7]" strokeWidth={2.5} />
          </button>
        </li>

        {/* Request my data */}
        <li>
          <button onClick={requestData} disabled={exporting} className="w-full flex items-center gap-4 px-5 py-5 text-left hover:bg-[#FAF8F2] transition-colors" data-testid="privacy-export">
            {exporting ? <Loader2 className="w-6 h-6 animate-spin text-[#3D424B]" /> : <Download className="w-6 h-6 text-[#3D424B]" strokeWidth={2} />}
            <span className="font-display font-semibold text-lg flex-1">{t("settings.requestData")}</span>
            <ChevronRight className="w-5 h-5 text-[#B9BDC7]" strokeWidth={2.5} />
          </button>
        </li>

        {/* Login methods (read-only) */}
        <li className="flex items-center gap-4 px-5 py-5" data-testid="privacy-login-methods">
          <KeyRound className="w-6 h-6 text-[#3D424B]" strokeWidth={2} />
          <span className="font-display font-semibold text-lg flex-1">{t("settings.loginMethods")}</span>
          <span className="text-[#7C828D]">{isGoogle ? "Google" : t("settings.loginEmail")}</span>
        </li>
      </ul>
    </div>
  );
}
